import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import HelpButton from './HelpButton';
import './Navigation.css';

export default function Navigation() {
  const { currentUser, userRoles, currentRole, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.navigation')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Check if user has admin role via custom claims
  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser) {
        setIsAdmin(false);
        return;
      }
      try {
        // First check userRoles from AuthContext
        if (userRoles && userRoles.length > 0) {
          const hasAdminInRoles = userRoles.map(String).map(r => r.toLowerCase()).includes('admin');
          if (hasAdminInRoles) {
            setIsAdmin(true);
            return;
          }
        }

        // Fallback: Check token claims directly (more reliable)
        let tr = await currentUser.getIdTokenResult();
        const hasAdminFromClaims = (() => {
          const claims = tr?.claims || {};
          const roles = claims.roles;
          if (Array.isArray(roles)) {
            return roles.map(String).map(r => r.toLowerCase()).includes('admin');
          }
          if (typeof roles === 'string') {
            return roles.toLowerCase() === 'admin';
          }
          return !!claims.admin;
        })();

        if (!hasAdminFromClaims) {
          // Force refresh in case claims were just updated
          tr = await currentUser.getIdTokenResult(true);
          const claims = tr?.claims || {};
          const roles = claims.roles;
          const refreshedAdmin =
            (Array.isArray(roles) && roles.map(String).map(r => r.toLowerCase()).includes('admin')) ||
            (typeof roles === 'string' && roles.toLowerCase() === 'admin') ||
            !!claims.admin;
          setIsAdmin(refreshedAdmin);
        } else {
          setIsAdmin(hasAdminFromClaims);
        }
      } catch (e) {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [currentUser, userRoles]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setIsMobileMenuOpen(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Determine which menu items to show based on user role
  const getMenuItems = () => {
    if (!currentUser || !userRoles || userRoles.length === 0) {
      return [
        { path: '/', label: t('navigation.home'), icon: '🏠' },
        { path: '/login', label: t('navigation.login'), icon: '🔐' },
        { path: '/signup', label: t('navigation.signup'), icon: '📝' },
      ];
    }

    const items = [];

    // Home is always available
    items.push({ path: '/', label: t('navigation.home'), icon: '🏠' });

    // Role-specific items
    if (userRoles.includes('freelancer') && (currentRole === 'freelancer' || !currentRole)) {
      items.push(
        { path: '/freelancer/dashboard', label: t('navigation.dashboard'), icon: '📊' },
        { path: '/freelancer/earnings', label: t('navigation.earnings'), icon: '💰' }
      );
    }

    if (userRoles.includes('business') && (currentRole === 'business' || currentRole === 'employer' || !currentRole)) {
      items.push(
        { path: '/business/dashboard', label: t('navigation.dashboard'), icon: '📊' }
      );
    }

    // Common items for all authenticated users
    const commonItems = [
      { path: '/chat', label: t('navigation.chat'), icon: '💭' },
      { path: '/profile', label: t('navigation.profile'), icon: '👤' }
    ];

    // Add jobs item only if user is not a business
    if (!userRoles.includes('business')) {
      commonItems.unshift({ path: '/jobs', label: t('navigation.jobs'), icon: '💼' });
    }

    items.push(...commonItems);

    // Admin items - check both userRoles and isAdmin state
    const hasAdminRole = userRoles && userRoles.length > 0 &&
      userRoles.map(String).map(r => r.toLowerCase()).includes('admin');
    if (hasAdminRole || isAdmin) {
      items.push({ path: '/admin', label: t('navigation.admin'), icon: '⚙️' });
    }

    return items;
  };

  // Check if InboxCounter is shown on this page (same logic as App.js)
  const shouldShowHelpInNav = currentUser &&
    !location.pathname.startsWith('/chat') &&
    !location.pathname.startsWith('/login') &&
    !location.pathname.startsWith('/admin') &&
    !location.pathname.startsWith('/auth-choice') &&
    !location.pathname.startsWith('/role-selection') &&
    !location.pathname.endsWith('/');

  const menuItems = getMenuItems();

  // Don't show navigation on homepage or public profile pages
  const shouldHideNav =
    location.pathname === '/' ||
    location.pathname.includes('/business/') && location.pathname.includes('/public') ||
    location.pathname.includes('/freelancer/') && location.pathname.includes('/public');

  if (shouldHideNav) {
    return null;
  }

  return (
    <nav className={`navigation ${isRTL ? 'rtl' : ''} ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
      <div className="nav-container">
        {/* Logo/Brand */}
        <Link to="/" className="nav-brand" onClick={() => setIsMobileMenuOpen(false)}>
          <img
            src="/favicon.ico"
            alt="SlaveX Logo"
            className="nav-logo"
          />
          <span className="nav-name">{t('app.name')}</span>
        </Link>

        {/* Desktop Menu */}
        <div className="nav-menu-desktop">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </Link>
          ))}
        </div>

        {/* Right side: Language Switcher and User Menu */}
        <div className="nav-right">
          <div className="nav-language">
            <LanguageSwitcher />
          </div>

          {currentUser && (
            <div className="nav-user-menu">
              <button
                className="nav-logout-btn"
                onClick={handleLogout}
                title={t('navigation.logout')}
              >
                <span className="nav-icon">🚪</span>
                <span className="nav-label-desktop">{t('navigation.logout')}</span>
              </button>
            </div>
          )}

          {/* Mobile Menu Toggle */}
          <button
            className="nav-mobile-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`nav-menu-mobile ${isMobileMenuOpen ? 'open' : ''}`}>
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`nav-link-mobile ${isActive(item.path) ? 'active' : ''}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
          </Link>
        ))}
        {shouldShowHelpInNav && (
          <div className="nav-link-mobile" onClick={() => setIsMobileMenuOpen(false)} style={{ cursor: 'pointer' }}>
            <HelpButton variant="inline" />
          </div>
        )}
        {currentUser && (
          <button
            className="nav-link-mobile nav-logout-mobile"
            onClick={handleLogout}
          >
            <span className="nav-icon">🚪</span>
            <span className="nav-label">{t('navigation.logout')}</span>
          </button>
        )}
      </div>
    </nav>
  );
}
